import { fmtArea, fmtEnergy, fmtPower } from "../../../utils/formatUnits";
import { EChartSvg } from "../../charts/EChartSvg";
import { areaWaterfallOption, cascadeOption } from "../charts/options";
import type {
  CascadeLevel,
  EnergyAnalysis,
  EnergyType,
} from "../../ReportComponent/model/types";
import type {
  GenerationMemorial,
  MemorialPremise,
  MemorialStep,
  RestrictionBreakdownResponse,
  RestrictionEntry,
} from "../../../services/api";
import {
  DataTable,
  Figure,
  INK,
  Lead,
  SubTitle,
  Unavailable,
  nf,
  pct,
} from "./primitives";

/** Acima disto o "não atribuído" deixa de ser ruído e vira sinal de falha (ver ADR 0002 da API). */
const UNATTRIBUTED_ALERT_PCT = 1;

export const ENERGY_TITLE: Record<EnergyType, string> = {
  solar: "Solar fotovoltaica",
  eolico: "Eólica",
};

function CascadeBlock({
  cascade,
  detailedLevel,
}: {
  cascade: CascadeLevel[];
  detailedLevel: string;
}) {
  return (
    <>
      <SubTitle>Cascata de níveis de projeção</SubTitle>
      <Lead>
        Cada nível herda as restrições do anterior e acrescenta as suas, então a área
        propícia só diminui ao longo da cascata. O nível {detailedLevel} é o detalhado
        nos memoriais a seguir.
      </Lead>
      <DataTable
        columns={[
          { header: "Nível", render: (r: CascadeLevel) => r.label },
          {
            header: "Área propícia",
            align: "right",
            render: (r) => (r.stats ? fmtArea(r.stats.propitious_area_km2) : "—"),
          },
          {
            header: "% da área",
            align: "right",
            render: (r) => (r.stats ? pct(r.stats.percentage) : "—"),
          },
          {
            header: "Potencial instalável",
            align: "right",
            render: (r) => (r.stats ? fmtPower(r.stats.installable_gw) : "—"),
          },
          {
            header: "Geração anual",
            align: "right",
            render: (r) => (r.stats ? fmtEnergy(r.stats.annual_energy_gwh) : "—"),
          },
        ]}
        rows={cascade}
        rowKey={(r) => r.layerName}
        emphasizeRow={(r) => r.label === detailedLevel}
      />
      {cascade.some((c) => c.stats) && (
        <Figure caption="Geração anual estimada em cada nível de projeção.">
          <EChartSvg
            ariaLabel="Geração anual por nível de projeção"
            width={520}
            height={190}
            option={cascadeOption(
              cascade.map((c) => ({
                label: c.label,
                energyGwh: c.stats?.annual_energy_gwh ?? null,
              })),
              fmtEnergy
            )}
          />
        </Figure>
      )}
    </>
  );
}

function AreaMemorialBlock({
  memorial,
}: {
  memorial: RestrictionBreakdownResponse | null;
}) {
  return (
    <>
      <SubTitle>Memorial de Cálculo da Área Propícia</SubTitle>
      {!memorial ? (
        <Unavailable what="Memorial de Cálculo da Área Propícia" />
      ) : (
        <>
          <Lead>
            Por que a área propícia ({pct(memorial.propitious_percentage)} da área, nível{" "}
            {memorial.level_label}) não é a área inteira. Cada porção de terreno excluída é
            atribuída a uma única restrição, na ordem decrescente de incidência: por isso a
            parcela atribuída pode ser menor que a incidência (o quanto a restrição atinge
            sozinha), e as parcelas somam exatamente a área excluída.
          </Lead>
          <Figure caption="Da área total à área propícia: parcela excluída por cada restrição, em % da área.">
            <EChartSvg
              ariaLabel="Waterfall da área total à área propícia"
              width={620}
              height={Math.max(160, 44 + 24 * (memorial.restrictions.length + 3))}
              option={areaWaterfallOption(memorial)}
            />
          </Figure>
          <DataTable
            columns={[
              {
                header: "Restrição",
                width: "40%",
                render: (r: RestrictionEntry) => (
                  <>
                    <div>{r.label}</div>
                    <div className="text-[0.7rem]" style={{ color: INK.secondary }}>
                      {r.available ? r.description : "Máscara indisponível"}
                    </div>
                  </>
                ),
              },
              { header: "Parcela atribuída", align: "right", render: (r) => pct(r.percentage, 2) },
              { header: "Área", align: "right", render: (r) => fmtArea(r.area_km2) },
              {
                header: "Incidência isolada",
                align: "right",
                render: (r) => pct(r.incidence_percentage, 2),
              },
            ]}
            rows={memorial.restrictions}
            rowKey={(r) => r.id}
          />
          <p className="text-[0.75rem] mt-1" style={{ color: INK.secondary }}>
            Área total {fmtArea(memorial.total_area_km2)} · área propícia{" "}
            {fmtArea(memorial.propitious_area_km2)} · não atribuído{" "}
            {pct(memorial.unattributed_percentage, 2)}
            {memorial.warning ? ` · ${memorial.warning}` : ""}
          </p>
          {memorial.unattributed_percentage > UNATTRIBUTED_ALERT_PCT && (
            <p className="text-[0.75rem] mt-1" style={{ color: INK.primary }}>
              ⚠ {pct(memorial.unattributed_percentage, 1)} da área excluída não é explicada
              por nenhuma restrição catalogada. Acima de {UNATTRIBUTED_ALERT_PCT}%, isso indica
              divergência entre o raster de projeção e as máscaras de restrição — os
              percentuais por restrição devem ser lidos como aproximados.
            </p>
          )}
        </>
      )}
    </>
  );
}

function formatPremise(p: MemorialPremise): string {
  if (typeof p.value === "string") return p.value;
  return `${nf(p.value, 3)}${p.unit ? ` ${p.unit}` : ""}`;
}

function formatStep(s: MemorialStep): string {
  switch (s.unit) {
    case "km²":
      return fmtArea(s.value);
    case "GW":
    case "GWp":
      return `${fmtPower(s.value)}${s.unit === "GWp" ? "p" : ""}`;
    case "GWh/ano":
      return `${fmtEnergy(s.value)}/ano`;
    case "":
      return s.id === "fator_capacidade" ? pct(s.value * 100, 1) : nf(s.value, 3);
    default:
      return `${nf(s.value, 3)} ${s.unit}`;
  }
}

function GenerationMemorialBlock({
  memorial,
}: {
  memorial: GenerationMemorial | null;
}) {
  return (
    <>
      <SubTitle>Memorial de Cálculo da Geração</SubTitle>
      {!memorial ? (
        <Unavailable what="Memorial de Cálculo da Geração" />
      ) : (
        <>
          <Lead>{memorial.method}</Lead>
          <DataTable
            columns={[
              { header: "Etapa", width: "24%", render: (s: MemorialStep) => s.label },
              {
                header: "Cálculo",
                render: (s) => (
                  <span className="font-mono text-[0.72rem]">{s.formula}</span>
                ),
              },
              { header: "Resultado", align: "right", width: "18%", render: formatStep },
            ]}
            rows={memorial.steps}
            rowKey={(s) => s.id}
            emphasizeRow={(s) => s.id === "energia_anual"}
          />
          {memorial.notes.map((note) => (
            <p key={note} className="text-[0.75rem] mt-1" style={{ color: INK.secondary }}>
              ⚠ {note}
            </p>
          ))}
        </>
      )}
    </>
  );
}

export function PremisesBlock({ memorial }: { memorial: GenerationMemorial | null }) {
  if (!memorial) return null;
  return (
    <>
      <SubTitle>Premissas</SubTitle>
      <DataTable
        columns={[
          { header: "Premissa", width: "34%", render: (p: MemorialPremise) => p.label },
          { header: "Valor", align: "right", width: "20%", render: formatPremise },
          {
            header: "Fonte",
            render: (p) => (
              <span className="text-[0.72rem]" style={{ color: INK.secondary }}>
                {p.source}
              </span>
            ),
          },
        ]}
        rows={memorial.premises}
        rowKey={(p) => p.id}
      />
    </>
  );
}

export function MemorialSection({
  analysis,
  levelLabel,
}: {
  analysis: EnergyAnalysis;
  levelLabel: string;
}) {
  return (
    <div>
      <CascadeBlock cascade={analysis.cascade} detailedLevel={levelLabel} />
      <AreaMemorialBlock memorial={analysis.areaMemorial} />
      <GenerationMemorialBlock memorial={analysis.generationMemorial} />
      <PremisesBlock memorial={analysis.generationMemorial} />
    </div>
  );
}
